import { CheckCircleFilled } from "@ant-design/icons"
import { Flex, Typography } from "antd"
import React, { type ReactNode } from "react"

const { Text, Title } = Typography

/** What a finished signature left the request as, read off the status the server answered with. */
export type SignedOutcome = "waiting" | "everyoneSigned" | "completed"

export const signedOutcomeOf = (envelopeStatus: string | undefined) =>
  envelopeStatus === "completed"
    ? "completed"
    : envelopeStatus === "signed"
      ? "everyoneSigned"
      : "waiting"

export interface SignedPanelProps {
  title: string
  documentName: string
  /** The request as it now stands, beside the document's name. */
  status?: ReactNode
  body: string
  /** Any further line, such as who is still to sign. */
  detail?: ReactNode
  actions?: ReactNode
}

/** The one screen after a signature is submitted, for the customer and the company alike. */
export function SignedPanel({
  title,
  documentName,
  status,
  body,
  detail,
  actions
}: SignedPanelProps) {
  return (
    <Flex
      vertical
      align="center"
      gap={12}
      role="status"
      className="mx-auto w-full max-w-[34rem] py-6 text-center"
    >
      <span
        aria-hidden
        className="flex size-14 items-center justify-center rounded-full bg-[var(--dk-success-bg,#ECFDF3)] text-3xl text-success"
      >
        <CheckCircleFilled />
      </span>
      <Title level={4} className="!mb-0">
        {title}
      </Title>
      <Flex align="center" justify="center" gap={8} wrap className="max-w-full">
        <Text
          strong
          ellipsis={{ tooltip: documentName }}
          className="max-w-full"
        >
          {documentName}
        </Text>
        {status}
      </Flex>
      <Text type="secondary">{body}</Text>
      {detail}
      {actions && (
        <Flex gap={8} wrap justify="center" className="mt-2">
          {actions}
        </Flex>
      )}
    </Flex>
  )
}
